import { getAllSubmissionsService } from "@/service/submission.service";
import { SubmissionModel, SubmissionQuery } from "@/types/submission.model";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import type { AxiosError } from "axios";

interface SubmissionState {
  submissions: SubmissionModel[];
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  noSubmissions: boolean;
  query: SubmissionQuery;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: SubmissionState = {
  submissions: [],
  noSubmissions: true,
  loading: false,
  actionLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
  query: {
    schoolId: "",
    teacherId: "",
    fromDate: "",
    toDate: "",
    page: 1,
    pageSize: 10,
  },
};

// Async Actions
export const fetchSubmissions = createAsyncThunk(
  "submission/fetchAll",
  async (query: SubmissionQuery, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const state = getState() as RootState;

      const { school_id, teacher_id } = state.auth.user || {};

      if (school_id) {
        query.schoolId = school_id;
      }

      if (teacher_id) {
        query.teacherId = teacher_id;
      }

      const {
        data: { data, pageInfo },
      } = await getAllSubmissionsService(query);
      dispatch(setLoading(false));

      dispatch(setNoSubmissions(false));
      dispatch(setQuery(query));

      return {
        submissions: data,
        // Handle different API response formats for pagination
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoSubmissions(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch submissions";
      dispatch(setError(message));
      throw error;
    }
  }
);

const submissionSlice = createSlice({
  name: "submission",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<SubmissionQuery>) => {
      state.query = action.payload;
    },
    setNoSubmissions: (state, action: PayloadAction<boolean>) => {
      state.noSubmissions = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.actionLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all submissions
      .addCase(fetchSubmissions.fulfilled, (state, action) => {
        state.submissions = action.payload.submissions;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSubmissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch submissions";
      });
  },
});

export const {
  setLoading,
  setActionLoading,
  setError,
  clearError,
  resetAllState,
  setNoSubmissions,
  setQuery,
} = submissionSlice.actions;

// Selectors
export const selectSubmissions = (state: RootState) =>
  state.submissions.submissions;
export const selectSubmissionLoading = (state: RootState) =>
  state.submissions.loading;
export const selectSubmissionActionLoading = (state: RootState) =>
  state.submissions.actionLoading;
export const selectSubmissionError = (state: RootState) =>
  state.submissions.error;
export const selectTotalItems = (state: RootState) =>
  state.submissions.pagination.totalItems;
export const selectPagination = (state: RootState) =>
  state.submissions.pagination;
export const selectNoSubmissions = (state: RootState) =>
  state.submissions.noSubmissions;
export const selectQuery = (state: RootState) => state.submissions.query;

export default submissionSlice.reducer;
